import type { Question } from "@/services/questionService";
import { normalizeQuestionOptions } from "./questionNormalizer";

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export function isWritingCompetency(value: unknown): boolean {
  const v = String(value || "").toLowerCase();

  return (
    v.includes("writing") ||
    v.includes("expression écrite") ||
    v.includes("expression ecrite") ||
    v === "ee"
  );
}

export function normalizeQuestionSubSection(value: unknown): string {
  const v = String(value || "").trim().toLowerCase();

  if (v === "phrases_lacunaires" || v === "textes_lacunaires" || v === "lecture_rapide_textes" || v === "lecture_rapide_graphiques") {
    return v;
  }

  if (v.includes("phrases_lacunaires") || v.includes("phrase")) {
    return "phrases_lacunaires";
  }

  if (v.includes("textes_lacunaires") || v.includes("texte lacunaire") || v.includes("textes lacunaires") || v.includes("lacunaire")) {
    return "textes_lacunaires";
  }

  if (v.includes("lecture_rapide_graphiques") || v.includes("graphique") || v.includes("graphiques")) {
    return "lecture_rapide_graphiques";
  }

  if (v.includes("lecture_rapide_textes") || v.includes("lecture rapide de textes")) {
    return "lecture_rapide_textes";
  }

  return "";
}

/**
 * Fallback to extract the specific question from the body text if the "question" field is missing.
 * Looks for the last sentence ending with a question mark.
 */
export function extractQuestionFromBody(body: string): { cleanBody: string; extractedQuestion: string | null } {
  if (!body) return { cleanBody: "", extractedQuestion: null };

  const trimmedBody = body.trim();
  
  // Basic check: must end with '?' to be considered a trailing question
  if (!trimmedBody.endsWith('?')) {
    return { cleanBody: body, extractedQuestion: null };
  }

  // Find the start of the last sentence ending in '?'
  // We search backwards for common sentence delimiters
  const lastIdx = trimmedBody.lastIndexOf('?');
  
  let startIdx = 0;
  for (let i = lastIdx - 1; i >= 0; i--) {
    if (['.', '!', '?', '\n', ':'].includes(trimmedBody[i])) {
      startIdx = i + 1;
      break;
    }
  }

  const extracted = trimmedBody.substring(startIdx).trim();
  const cleanBody = trimmedBody.substring(0, startIdx).trim();

  // If extraction seems invalid (e.g., just the '?'), return original
  if (extracted.length <= 1) {
    return { cleanBody: body, extractedQuestion: null };
  }

  return { 
    cleanBody: cleanBody || "", 
    extractedQuestion: extracted 
  };
}

/**
 * Validates a question object against mandatory field requirements.
 * Supports both internal schema and various import field names.
 */
export const validateQuestion = (q: any, context?: { competence?: string; section?: string }): ValidationResult => {
  const errors: string[] = [];
  const comp = (q.competence || q.competency || context?.competence || "").toLowerCase();
  const sect = q.section || context?.section || "";

  // 1. Core Metadata
  if (!String(q.title || "").trim()) {
    errors.push("O título da questão é obrigatório.");
  }

  if (!comp) {
    errors.push("A competência (Reading, Listening ou Writing) é obrigatória.");
  }

  if (!sect) {
    errors.push("A seção da prova é obrigatória.");
  }

  // 2. Question Prompt (The specific task/question)
  const questionPrompt = String(
    q.question || 
    q.prompt || 
    q.instruction || 
    q.command || 
    q.statement || 
    q.enonce || 
    q.enoncé || 
    q.task || 
    q.metadata?.question || 
    q.metadata?.prompt || 
    ""
  ).trim();

  if (!questionPrompt) {
    errors.push("O enunciado ou pergunta específica da questão é obrigatório.");
  }

  // 3. Main Text / Document Body (Mandatory for Reading)
  const bodyText = String(
    q.text || 
    q.body || 
    q.passage || 
    q.content || 
    q.bodyText || 
    q.metadata?.text || 
    ""
  ).trim();

  if (comp.includes("reading") || comp.includes("ce")) {
    // In CE, some sections like 'Lecture rapide' might rely purely on the image, 
    // but the 'question' field (prompt) is still required.
    // However, usually a body or text is expected unless it's a graphic-only question.
    if (!bodyText && sect !== "Lecture rapide de textes et de graphiques") {
      errors.push("O texto principal ou documento da questão é obrigatório para Reading.");
    }
  }

  // 4. MCQ specific validations
  const isWriting = isWritingCompetency(comp);
  const isMcq = q.type === "mcq" || q.type === "multiple_choice" || (!q.type && !isWriting);

  if (isMcq && !isWriting) {
    // Options validation
    const options = q.options || q.alternatives || q.choices || q.metadata?.options || null;
    
    if (!options || typeof options !== "object") {
      errors.push("As alternativas (A, B, C, D) são obrigatórias para questões de múltipla escolha.");
    } else {
      const keys = ["A", "B", "C", "D"];
      for (const key of keys) {
        const optVal = options[key] !== undefined ? options[key] : (Array.isArray(options) ? options.find((o: any) => o.label === key)?.text : null);
        if (optVal === null || optVal === undefined || String(optVal).trim() === "") {
          // Exception: Listening Section A might have images instead of text
          let hasImage = false;
          if (Array.isArray(options)) {
            const optObj = options.find((o: any) => o.label === key || o.id === key);
            hasImage = !!(optObj?.imageUrl || optObj?.mediaId || optObj?.image || q[`option${key}Image`]);
          } else {
            const optObj = options[key];
            hasImage = !!(
              optObj?.imageUrl || 
              optObj?.mediaId || 
              optObj?.image || 
              q[`option${key}Image`] ||
              (typeof optObj === "string" && (optObj.startsWith("data:") || optObj.startsWith("http") || optObj.endsWith(".png") || optObj.endsWith(".jpg") || optObj.endsWith(".jpeg") || optObj.endsWith(".webp")))
            );
          }
          if (!hasImage) {
            errors.push(`A alternativa ${key} está vazia.`);
          }
        }
      }
    }

    // Correct Answer validation
    const correctAnswer = String(
      q.correctAnswer || 
      q.correct_answer || 
      q.correctOption || 
      q.answer || 
      q.gabarito || 
      q.metadata?.correctAnswer || 
      ""
    ).trim().toUpperCase();

    if (!["A", "B", "C", "D"].includes(correctAnswer)) {
      errors.push("A resposta correta deve ser A, B, C ou D.");
    }
  }

  // 5. Explanation validation
  if (!isWriting) {
    const explanation = String(
      q.explanation || 
      q.feedback || 
      q.justification || 
      q.metadata?.explanation || 
      ""
    ).trim();

    if (!explanation) {
      errors.push("A explicação da resposta (gabarito comentado) é obrigatória.");
    }
  }

  // 6. Section Specific Rules (Subsections)
  const subSection = q.subSection || q.sub_section || q.metadata?.subSection || q.metadata?.sub_section || "";
  
  if (sect === "Phrases lacunaires / Textes lacunaires") {
    if (!["phrases_lacunaires", "textes_lacunaires"].includes(subSection)) {
      errors.push("O subtipo (phrases_lacunaires ou textes_lacunaires) é obrigatório para esta seção.");
    }
  }

  if (sect === "Lecture rapide de textes et de graphiques") {
    if (!["lecture_rapide_textes", "lecture_rapide_graphiques"].includes(subSection)) {
      errors.push("O subtipo (lecture_rapide_textes ou lecture_rapide_graphiques) é obrigatório para esta seção.");
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Normalizes a question object to the internal schema used by the application.
 */
export const normalizeQuestion = (q: any, context?: { competence?: string; section?: string }): any => {
  const comp = (q.competence || q.competency || context?.competence || "Reading");
  const formattedComp = (comp.charAt(0).toUpperCase() + comp.slice(1).toLowerCase()) as "Reading" | "Listening" | "Writing";
  
  const sect = q.section || context?.section || "";
  
  const instruction = String(q.instruction || "").trim();
  const questionPrompt = String(q.question || q.prompt || q.command || q.statement || q.metadata?.question || "").trim();
  const bodyText = String(q.text || q.body || q.passage || q.content || q.bodyText || q.metadata?.text || "").trim();
  
  const explanation = String(q.explanation || q.feedback || q.justification || q.metadata?.explanation || "").trim();
  const correctAnswer = String(q.correctAnswer || q.correct_answer || q.answer || q.metadata?.correctAnswer || "A").trim().toUpperCase();

  const optionsFormatted = normalizeQuestionOptions(q);

  const subSection = normalizeQuestionSubSection(
    q.subSection || q.sub_section || q.metadata?.subSection || q.metadata?.sub_section || ""
  );

  const isWriting = isWritingCompetency(comp);

  return {
    ...q,
    title: String(q.title || "").trim(),
    competence: formattedComp,
    competency: formattedComp.toLowerCase(),
    section: sect,
    instruction: instruction,
    question: questionPrompt, // Specific prompt
    text: bodyText,
    bodyText: bodyText,
    correctAnswer: isWriting ? null : correctAnswer,
    explanation: isWriting ? null : explanation,
    subSection: subSection || undefined,
    options: isWriting ? null : optionsFormatted,
    metadata: {
      ...(q.metadata || {}),
      subSection: subSection || undefined
    },
    // Ensure tags are an array
    tags: Array.isArray(q.tags) ? q.tags : (typeof q.tags === 'string' ? q.tags.split(',').map(t => t.trim()) : [])
  };
};
